import styled from "styled-components";

/**
 * Shared styled-components utilities
 *
 * These are common patterns used across the codebase.
 * All values should reference design tokens from design-tokens.css
 * to maintain consistency and make updates easier.
 *
 * USAGE GUIDELINES:
 * - Use styled-components for: component-scoped styles, props-based styling, or when extending base components
 * - Use CSS classes (App.css) for: static utilities, simple one-off styles, or when className is preferred
 * - Prefer CSS classes for simple utilities (e.g., .absolute, .text-ellipsis) - they're simpler and more performant
 * - Use styled-components when you need to extend a base component or add props-based styling
 */

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

/**
 * Flex container with space-between
 */
export const FlexBetween = styled.div`
  display: flex;
  align-content: space-between;
  justify-content: space-between;
`;

/**
 * Flex container with column direction
 */
export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Flex container with row direction (default)
 */
export const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

/**
 * Left-aligned content container
 */
export const LeftContent = styled.div`
  text-align: left;
`;

/**
 * Right-aligned content container
 */
export const RightContent = styled.div`
  text-align: right;
`;

/**
 * Center-aligned content container
 */
export const CenterContent = styled.div`
  text-align: center;
`;

// ============================================================================
// POSITIONING
// ============================================================================

/**
 * Fixed positioned container for alerts/banners that should always be visible
 */
export const StickyDiv = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
`;

// ============================================================================
// FORM ELEMENTS
// ============================================================================

/**
 * Input with iOS zoom prevention (16px minimum)
 */
export const BaseInput = styled.input`
  font-size: 1rem; /* 16px - prevents iOS zoom */
  box-sizing: border-box;
`;

/**
 * Dropdown/select input
 */
export const DropdownInput = styled(BaseInput)`
  display: block;
  width: 100%;
`;

// ============================================================================
// MODAL/OVERLAY COMPONENTS
// ============================================================================

/**
 * Grayed out modal background
 */
export const GrayedOutModal = styled.div`
  background-color: gray;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: lightgreen;
`;

/**
 * Modal content container
 */
export const ModalContent = styled.div`
  font-size: 1.5em;
  text-align: center;
`;

// ============================================================================
// ICONS
// ============================================================================

/**
 * Clickable icon with border
 */
export const ClickableIcon = styled.i`
  color: white;
  padding: 0 1em;
  border-style: solid;
  border-color: green;
  border-width: 0 0 0 2px;
`;

/**
 * Clickable icon without border
 */
export const ClickableIconNoBorder = styled.i`
  color: white;
  padding: 0 1em;
`;
